import { Component, computed, Input, TemplateRef, ViewChild } from '@angular/core';
import { ModalController, Platform } from '@ionic/angular';
import { AlertService, ArmyBuilderConfig, Modal, ModalComponent, PurchasingService, SettingsService } from 'army-builder-shared';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { CancelSubscriptionComponent } from './cancel-subscription.component';
import { SubscriptionService } from './subscription.service';
import { Router } from '@angular/router';
import { PaywallComponent } from './paywall.component';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
    standalone: false,
    selector: 'wl-manage-subscription',
    template: `
        <ng-container
            *ngLet="{
                hasExpired: hasExpired$ | async,
                hasAccess: hasAccess$ | async,
                user: user$ | async,
                expiry: expiry$ | async,
                expiryProgress: expiryProgress$ | async,
                expiryProgressColour: expiryProgressColour$ | async,
                newCustomer: newCustomer$ | async,
                monthlySubscription: monthlySubscription$ | async
            } as props"
        >
            @if (!props.newCustomer) {
                @if (props.user?.isPromotionalSubscription) {
                    <ion-card color="tertiary">
                        <ion-card-content>
                            You are on a promotional subscription granted on {{ props.user?.promotionalGrantDate | date }}.
                        </ion-card-content>
                    </ion-card>
                } @else if (props.user?.isCancelledDueToBillingIssue) {
                    <ion-card color="danger">
                        <ion-card-content>
                            Your subscription was cancelled due to a billing issue. Please subscribe again.
                        </ion-card-content>
                    </ion-card>
                } @else if (props.user?.hasActiveBillingIssue) {
                    <ion-card color="danger" class="no-gutter">
                        <ion-card-content>
                            @if (isNativeWithStripe()) {
                                We're having trouble processing your subscription renewal. Please log into the web app to update your
                                payment details.
                            } @else {
                                We're having trouble processing your subscription renewal. Please update your payment details below.
                            }
                        </ion-card-content>
                    </ion-card>
                } @else if (props.hasExpired && props.expiry) {
                    <ion-card color="danger">
                        <ion-card-content> Your subscription expired {{ props.expiry | relativeDate }}. </ion-card-content>
                    </ion-card>
                } @else if (props.expiry) {
                    <div class="subscription-status">
                        @if (props.user.roles.includes('app_subscriber_annual')) {
                            <p class="tier-label">Annual subscriber</p>
                        } @else if (props.user.roles.includes('app_subscriber')) {
                            <p class="tier-label">Monthly subscriber</p>
                        }
                        <p class="expiry-text">
                            Expires {{ props.expiry | relativeDate }} &middot; renews {{ props.expiry | date: 'd MMM yyyy' }}
                        </p>
                        <ion-progress-bar [color]="props.expiryProgressColour" [value]="props.expiryProgress"></ion-progress-bar>
                    </div>
                }

                <div class="buttons vertical">
                    @if (props.user.roles.includes('app_subscriber_annual')) {
                        <ng-template #voucherDetails>
                            <voucher-details></voucher-details>
                        </ng-template>
                    } @else if (props.user.roles.includes('app_subscriber') && web) {
                        <ion-button (click)="subscribe()">Upgrade to Annual</ion-button>
                    }

                    <!--
                        Subscribing again is the only thing that restores lapsed access,
                        whatever ended it. The Stripe portal cannot restart a cancelled
                        subscription, so offering it here just dead-ends the customer —
                        it belongs to people who still have a subscription to manage.
                    -->
                    @if (!props.hasAccess) {
                        <ion-button (click)="subscribe()">Subscribe</ion-button>
                    }

                    <form
                        action="{{ config.apiBaseUrl }}/purchase/createPortalSession"
                        method="POST"
                        *ngIf="web && props.hasAccess && !props.user?.isPromotionalSubscription"
                    >
                        <input type="hidden" name="accessToken" value="{{ props.user?.accessToken }}" />
                        <ion-button type="submit">Manage Subscription</ion-button>
                    </form>

                    <ion-button (click)="refreshPurchases()">Refresh Subscription</ion-button>
                </div>

                @if (nativeManagementHint()) {
                    <p class="platform-hint">{{ nativeManagementHint() }}</p>
                }

                @if (props.user.roles.includes('app_subscriber_annual') && showVoucher() && !hideVoucher) {
                    <div class="voucher-block">
                        <p class="voucher-label">
                            As an annual subscriber, you have access to an exclusive discount voucher for the Warlord Games web store.
                        </p>
                        <div class="buttons">
                            <ion-button (click)="showVoucherDetails()">View Discount Voucher</ion-button>
                        </div>
                    </div>
                }
            } @else {
                <div class="cta">
                    <h2 class="cta-title">{{ 'GLOBAL.SUBSCRIPTION.NEW_CUSTOMER.TITLE' | translate }}</h2>
                    <p class="cta-text">{{ 'GLOBAL.SUBSCRIPTION.NEW_CUSTOMER.DESCRIPTION' | translate }}</p>
                    <div class="buttons">
                        <ion-button (click)="subscribe()">{{ 'GLOBAL.SUBSCRIPTION.SUBSCRIBE' | translate }}</ion-button>
                    </div>
                    <p class="refresh-link">
                        {{ 'GLOBAL.SUBSCRIPTION.NEW_CUSTOMER.REFRESH_PROMPT' | translate }}
                        <a (click)="refreshPurchases()">Refresh Subscription</a>
                    </p>
                </div>
            }

            <p class="refresh-hint no-gutter">
                If your subscription is not reflected in the app, try refreshing above. If that doesn't work, email
                <a href="mailto:warlord@maloric.com">warlord&#64;maloric.com</a>.
            </p>

            <!-- <ion-button (click)="checkSubscription()">Check Subscription</ion-button> -->
            <ng-container *ngIf="!stripeCheckout">
                <div *ngIf="subscription$ | async as product" class="buttons vertical">
                    <ion-button color="danger" *ngIf="product.type === 'PAID_SUBSCRIPTION' && isOwned(product.key)" (click)="cancelSub()">
                        {{ 'GLOBAL.SUBSCRIPTION.CANCEL' | translate }}
                    </ion-button>
                </div>
            </ng-container>
        </ng-container>
    `,
    styles: [
        `
            .subscription-status {
                background: var(--ion-color-light, #f4f5f8);
                border-radius: 8px;
                padding: 12px;
                margin-bottom: 12px;
            }

            .tier-label {
                font-weight: 600;
                margin: 0 0 2px;
            }

            .expiry-text {
                font-size: 0.9em;
                color: var(--ion-color-medium);
                margin: 0 0 8px;
            }

            ion-progress-bar {
                border-radius: 4px;
            }

            .voucher-block {
                background: var(--ion-color-light, #f4f5f8);
                border-radius: 8px;
                padding: 12px;
                margin-top: 12px;
            }

            .voucher-label {
                font-size: 0.9em;
                margin: 0 0 8px;
            }

            .buttons {
                margin-left: 0;
                margin-right: 0;
            }

            .buttons form {
                width: 100%;
            }

            .refresh-hint {
                font-size: 0.85em;
                color: var(--ion-color-medium);
                margin-top: 12px;
            }

            .platform-hint {
                font-size: 0.85em;
                color: var(--ion-color-medium);
                text-align: center;
                margin: 4px 0 12px;
            }

            .cta {
                text-align: center;
                padding: 32px 0 8px;
            }

            .cta-title {
                margin: 0 0 8px;
                font-size: 1.3em;
            }

            .cta-text {
                margin: 0 0 4px;
                color: var(--ion-color-medium);
            }

            .refresh-link {
                text-align: center;
                margin-top: 4px;
                font-size: 0.9em;
                color: var(--ion-color-medium);
            }

            .refresh-link a {
                cursor: pointer;
            }
        `
    ]
})
export class ManageSubscriptionComponent {
    @Input() hideVoucher = false;
    hasExpired$ = this.subscriptionService.hasExpired$;
    hasAccess$ = this.subscriptionService.userHasAccess$;
    newCustomer$ = this.subscriptionService.newCustomer$;
    stripeCheckout = !(this.platform.is('android') || this.platform.is('ios'));
    subscription$ = this.purchasingService.products$.pipe(map((p) => p[0]));
    status$: Observable<string[]> = this.purchasingService.status$;
    user$ = this.settingsService.login$.pipe(map((l) => l.user));
    expiry$ = this.subscriptionService.expiry$;
    expiryProgress$ = this.expiry$.pipe(
        map((e) => {
            const daysRemaining = Math.floor((e?.getTime() - Date.now()) / 1000 / 60 / 60 / 24);
            return daysRemaining / 28;
        })
    );

    showVoucher = toSignal(this.user$.pipe(map((u) => u?.roles.includes('app_subscriber_annual'))));

    expiryProgressColour$ = this.expiryProgress$.pipe(
        map((e) => {
            if (e > 0.5) {
                return 'success';
            }
            if (e > 0.25) {
                return 'warning';
            }
            return 'danger';
        })
    );

    web = !this.platform.is('cordova');
    isNativeWithStripe = computed(() => !this.stripeCheckout && this.subscriptionService.subscriptionPlatform() === 'WEB');
    nativeManagementHint = computed(() => {
        if (this.stripeCheckout) return null;
        const platform = this.subscriptionService.subscriptionPlatform();
        if (platform === 'IOS') return 'To manage your subscription, use the App Store.';
        if (platform === 'ANDROID') return 'To manage your subscription, use Google Play.';
        return null;
    });
    monthlySubscription$ = this.purchasingService.products$.pipe(
        map((products) => {
            return products.find((p) => p.key === 'app_sub');
        })
    );

    @ViewChild('voucherDetails')
    voucherDetails: TemplateRef<any>;

    constructor(
        public config: ArmyBuilderConfig,
        private subscriptionService: SubscriptionService,
        private platform: Platform,
        private purchasingService: PurchasingService,
        private modal: Modal,
        private settingsService: SettingsService,
        private router: Router,
        private alertService: AlertService
    ) {}

    purchase(productId: string) {
        this.purchasingService.purchase(productId);
    }

    subscribe() {
        this.modal.show({
            component: PaywallComponent
        });
    }

    isOwned(productId: string) {
        return this.purchasingService.isOwned(productId);
    }

    async cancelSub() {
        this.modal.show({
            component: CancelSubscriptionComponent
        });
    }

    async refreshPurchases() {
        const result = await this.purchasingService.restorePurchases();
        if (!result.success) {
            await this.alertService.showAlert(
                'Restore Failed',
                'Unable to restore purchases from the store. Please check your connection and try again, or contact support at warlord@maloric.com.'
            );
        }
        this.router.navigateByUrl('/subscription/handle-purchase');
    }

    async showVoucherDetails() {
        this.modal.show({
            component: ModalComponent,
            props: {
                title: 'Voucher Redemption',
                contentTemplate: this.voucherDetails,
                cssClass: 'home'
            }
        });
    }
}
